import type { FC, HTMLProps } from "react";
import { useTranslation } from "react-i18next";

import { Typography } from "@aviary";
import { Price } from "@shared/components/Price";
import { PricingInformation } from "@shared/components/PricingInformation";
import { useBreakpoints } from "@shared/hooks";

import * as styles from "./ProductUnitPricing.styles";

interface Props extends HTMLProps<HTMLDivElement> {
  msrp?: number;
  sellPrice?: number;
  promotionPrice?: number;
  unitPostfix?: string;
  isWholesale?: boolean;
  currencySymbol?: "CAN" | "US";
  isStackedOnMobile?: boolean;
  isPromoActive?: boolean;
  isStacked?: boolean;
}

const ProductUnitPricing: FC<Props> = ({
  sellPrice,
  msrp,
  promotionPrice,
  unitPostfix,
  isWholesale,
  currencySymbol,
  isStackedOnMobile,
  isPromoActive,
  isStacked,
}) => {
  const { t } = useTranslation("products");
  const { phoneLarge } = useBreakpoints();
  const isMobile = phoneLarge.lessThan;

  const renderPromotionPrice = () =>
    isMobile ? (
      <s>
        <Price price={sellPrice} />
      </s>
    ) : (
      <>
        &nbsp;
        <s>
          <Price price={sellPrice} />
        </s>
      </>
    );

  const renderPrice = () =>
    isMobile && promotionPrice ? (
      <>
        <div css={styles.mobilePrice}>
          <Price price={promotionPrice} css={styles.wholesalePrice} />
          <Typography css={styles.unit}>{unitPostfix}</Typography>
        </div>
        <Typography css={styles.unitMobile}>
          {renderPromotionPrice()} | {t("MSRP")}:&nbsp;
          <Price price={msrp} />
        </Typography>
      </>
    ) : (
      <>
        <Price price={promotionPrice ?? sellPrice} css={styles.wholesalePrice} />
        <Typography css={styles.unit}>
          {unitPostfix}
          {promotionPrice && renderPromotionPrice()} | {t("MSRP")}:&nbsp;
          <Price price={msrp} />
        </Typography>
      </>
    );

  if (isWholesale) {
    return (
      <div
        css={[
          styles.productUnitPricing(isStackedOnMobile),
          promotionPrice && styles.productUnitPricingPromotion,
        ]}
        data-testid="wholesale-price"
      >
        {renderPrice()}
      </div>
    );
  }

  return (
    <div
      css={[
        styles.productUnitPricing(isStackedOnMobile),
        isStacked && styles.stacked,
        unitPostfix && styles.wrapText,
      ]}
    >
      <PricingInformation
        isPromoActive={isPromoActive}
        sellPrice={sellPrice}
        msrp={msrp}
        currencySymbol={currencySymbol}
      />
      {unitPostfix && <Typography css={styles.unit}>({unitPostfix})</Typography>}
    </div>
  );
};

export { ProductUnitPricing };
